import lodash from 'lodash';
import {composeReducers} from 'common/util/composeReducers';
import {
    ADMIN_LOAD_INSTITUTIONS,
    ADMIN_LOAD_INSTITUTION,
    ADMIN_CREATE_INSTITUTION,
    ADMIN_UPDATE_INSTITUTION,
    ADMIN_LOAD_TIMEZONES,
    ADMIN_INSTITUTION_PROPERTY_ON_CHANGE,
    ADMIN_VALUE_ONCHANGE,
    BINDER_DATASOURCE_OVERRIDES_SET_AVAILABLE_OPTIONS,
    BINDER_DATASOURCE_OVERRIDES_SET_SELECTED_OPTIONS
} from 'institutionalAdmin/actions/institutions';

export function loadInstitutions(state, action) {
    if (!action || action.type !== ADMIN_LOAD_INSTITUTIONS) {
        return state;
    }

    const institutions = action.payload;
    if (!institutions) {
        return state;
    }

    //values should be keyed by the institution id
    const currentInstitutions = lodash.values(lodash.get(state, ['institutions', 'byId']));
    if (lodash.isEqual(currentInstitutions, institutions)) {
        return state;
    }

    var institutionsKeyedById = lodash.keyBy(institutions, 'id');

    return {
        ...state,
        institutions: {
            ...state.institutions,
            byId: institutionsKeyedById
        }
    };
}

export function loadInstitution(state, action) {
    if (!action || action.type !== ADMIN_LOAD_INSTITUTION) {
        return state;
    }

    const institution = action.payload;
    if (!institution) {
        return state;
    }

    const currentInstitution = lodash.get(state, ['institutions', 'byId', institution.id]);
    if (lodash.isEqual(currentInstitution, institution)) {
        return state;
    }

    return {
        ...state,
        institutions: {
            ...lodash.get(state, 'institutions'),
            byId: {
                ...lodash.get(state, 'institutions.byId'),
                [institution.id]: institution
            }
        }
    };
}

export function createInstitution(state, action) {
    if (!action || action.type !== ADMIN_CREATE_INSTITUTION) {
        return state;
    }

    const institution = action.payload;
    if (!institution || !institution.id) {
        return state;
    }

    const currentInstitutions = lodash.get(state, ['institutions', 'byId']);
    if (lodash.some(currentInstitutions, dt => lodash.isEqual(dt.id, institution.id))) {
        return state;
    }

    return {
        ...state,
        institutions: {
            byId: {
                ...lodash.get(state, ['institutions', 'byId']),
                [institution.id]: institution
            }
        }
    };
}

export function updateInstitution(state, action) {
    if (!action || action.type !== ADMIN_UPDATE_INSTITUTION) {
        return state;
    }

    const institution = action.payload;
    if (!institution || !institution.id) {
        return state;
    }

    const currentInstitution = lodash.get(state, ['institutions', 'byId', institution.id]);
    if (!currentInstitution) {
        return state;
    }

    const updateProperties = Object.keys(institution);
    const noPropertyChanged = lodash.isEqual(
        lodash.pick(currentInstitution, updateProperties),
        lodash.pick(institution, updateProperties)
    );

    if (noPropertyChanged) {
        return state;
    }

    return {
        ...state,
        institutions: {
            ...lodash.get(state, 'institutions'),
            byId: {
                ...lodash.get(state, 'institutions.byId'),
                [institution.id]: {
                    ...lodash.get(state, ['institutions', 'byId', institution.id]),
                    ...institution
                }
            }
        }
    };
}

export function getTimezoneList(state, action) {
    if (!action || action.type !== ADMIN_LOAD_TIMEZONES) {
        return state;
    }

    const timezones = action.payload;

    const formattedTimezones = timezones.map((timezone) => {
        return {
            name: `${timezone.name} ${timezone.offset}`,
            value: timezone.name
        };
    });

    return {
        ...state,
        institutions: {
            ...lodash.get(state, 'institutions'),
            byId: {
                ...lodash.get(state, ['institutions', 'byId'])
            },
            timezones: formattedTimezones
        }
    };
}

export function institutionObjectOnChange(state, action) {
    if (!action || action.type !== ADMIN_VALUE_ONCHANGE) {
        return state;
    }
    const {institution, key, value }  = action.payload;
    const currentInstitution = institution.id;

    return {
        ...state,
        institutions : {
            ...lodash.get(state, 'institutions'),
            byId: {
                ...lodash.get(state, ['institutions', 'byId']),
                [currentInstitution]: {
                    ...lodash.get(state, ['institutions', 'byId', currentInstitution]),
                    [key] : value,
                }
            },
        },
    };
}

export function institutionPropertyOnChange(state, action) {
    if ( !action || action.type !== ADMIN_INSTITUTION_PROPERTY_ON_CHANGE){
        return state;
    }
    const institution = action.payload.institution;
    const currentInstitution = institution.id;
    const properties = action.payload.properties;
    return {
        ...state,
        institutions : {
            ...lodash.get(state, 'institutions'),
            byId: {
                ...lodash.get(state, ['institutions', 'byId']),
                [currentInstitution]: {
                    ...lodash.get(state, ['institutions', 'byId', currentInstitution]),
                    properties,
                }
            },
        },
    };
}

export function setDatasourceOverridesAvailableOptions(state, action) {
    if ( !action || action.type !== BINDER_DATASOURCE_OVERRIDES_SET_AVAILABLE_OPTIONS){
        return state;
    }

    return {
        ...state,
        availableDatasourceOverrides: action.payload
    };
}

export function setDatasourceOverridesSelectedOptions(state, action) {
    if ( !action || action.type !== BINDER_DATASOURCE_OVERRIDES_SET_SELECTED_OPTIONS){
        return state;
    }

    return {
        ...state,
        selectedDatasourceOverrides: lodash.unionBy(action.payload, 'label'),
    };
}

export default composeReducers(
    loadInstitutions,
    loadInstitution,
    createInstitution,
    updateInstitution,
    getTimezoneList,
    institutionObjectOnChange,
    institutionPropertyOnChange,
    setDatasourceOverridesAvailableOptions,
    setDatasourceOverridesSelectedOptions
);
